import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { useManagement } from "../context/ManagementContext";
import "../styles/Home.css";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Modal from "react-modal";
import ProductsByCategoryPDFButton from "./ProductsByCategoryPDFButton";
import { db } from "../config/fbConfig";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc
} from "firebase/firestore";

Modal.setAppElement("#root");

const pages = [
  { name: "Accounts", icon: "1.", link: "accounts" },
  { name: "Journal Entries", icon: "2.", link: "journal-entries" },
  { name: "General Ledger", icon: "3.", link: "general-ledger" },  
];

const pages1 = [
  { name: "Petty Cash", icon: "1.", link: "petty-cash" },
  { name: "Bank Reconciliation", icon: "2.", link: "bank-reconciliation" },
];

const pages2 = [
  { name: "Vendors", icon: "1.", link: "vendors" },
  { name: "Purchase Orders", icon: "2.", link: "purchase-orders" },
  { name: "Bills", icon: "3.", link: "bills" },  
];

const pages3 = [
  { name: "Customers", icon: "1.", link: "customers" },
  { name: "Invoicing", icon: "2.", link: "invoicing" },
  { name: "Payment Tracking", icon: "3.", link: "payment-tracking" },
  { name: "Income Tracking", icon: "4.", link: "income-tracking" },
];

const financialReports = [
  { name: "Trial Balance", icon: "1.", link: "trial-balance" },
  { name: "Balance Sheet", icon: "2.", link: "balance-sheet" },
  { name: "Income Statement", icon: "3.", link: "income-statement" },
  { name: "Cash Flow Statement", icon: "4.", link: "cash-flow-statement" },
  { name: "Financial Ratios", icon: "5.", link: "financial-ratios" },
];

const pages4 = [
  { name: "Departments", icon: "1.", link: "departments" },
  { name: "Salary Structure", icon: "2.", link: "salary-structure" },
  { name: "Ranks & Pay", icon: "3.", link: "ranks" },
  { name: "Staff", icon: "4.", link: "staff" },  
  { name: "Payroll", icon: "5.", link: "payroll" },
  { name: "Salary", icon: "6.", link: "salary" },
];

const pages5 = [
  { name: "Categories", icon: "1.", link: "categories" },
  { name: "Units", icon: "2.", link: "units" },
  { name: "Products", icon: "3.", link: "products" },
];

const Management = () => {
  const { user, userData, loading: authLoading } = useUserAuth();
  const { managementData, setManagementData, loading, setLoading } = useManagement();
  const navigate = useNavigate();

  // Separate modals
  const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");

    // NEW: Selective Clear Modals
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [clearStatus, setClearStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const collectionsToClear = [
    { id: "accounts", label: "Chart of Accounts" },
    { id: "journalEntries", label: "Journal Entries" },
    { id: "invoices", label: "Invoices" },
    { id: "bills", label: "Bills" },
    { id: "purchaseOrders", label: "Purchase Orders" },
    { id: "sales", label: "Sales History" },
    { id: "payrollRecords", label: "Payroll Records" },
  ];

  useEffect(() => {
    setLoading(false); // keep your original behavior

    // Only fetch products if user can see Product Management
    if (!userData || !["Cashier", "Admin", "Supreme"].includes(userData.status)) {
      return;
    }

    const loadProductsForPDF = async () => {
      try {
        const productsSnap = await getDocs(collection(db, "products"));
        const productsList = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const productsPerCategory = {};
        productsList.forEach(product => {
          const cat = product.category || "Uncategorized";
          if (!productsPerCategory[cat]) {
            productsPerCategory[cat] = {
              count: 0,
              totalItems: 0,
              totalCostPrice: 0,
              totalSellingPrice: 0,
              totalProfit: 0,
              activeCount: 0,
              inactiveCount: 0,
            };
          }
          productsPerCategory[cat].count += 1;
          if (product.isActive) {
            productsPerCategory[cat].activeCount += 1;
            const qty = product.quantity || 0;
            const cost = product.costPrice || product.price || 0;
            const sell = product.price || 0;
            productsPerCategory[cat].totalItems += qty;
            productsPerCategory[cat].totalCostPrice += qty * cost;
            productsPerCategory[cat].totalSellingPrice += qty * sell;
            productsPerCategory[cat].totalProfit += qty * (sell - cost);
          } else {
            productsPerCategory[cat].inactiveCount += 1;
          }
        });

        setManagementData(prev => ({
          ...prev,
          products: productsList,
          posStats: { ...prev.posStats, productsPerCategory },
        }));
      } catch (err) {
        console.error("Failed to load products for PDF:", err);
      }
    };

    loadProductsForPDF(); // call the function correctly
  }, [userData, setManagementData, setLoading]);

  // Generate months up to current month
  const getMonthsSoFar = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months.slice(0, currentMonth + 1).map((month, index) => ({
      name: month,
      value: `${currentYear}-${(index + 1).toString().padStart(2, "0")}`
    }));
  };

  const months = getMonthsSoFar();

  // Handle Daily Sales Date Selection
  const handleDateSelect = () => {
    if (!selectedDate) {
      alert("Please select a date");
      return;
    }
    setIsDateModalOpen(false);
    navigate(`/sales-history?date=${selectedDate}`);
  };

    // NEW: Handle collection checkbox toggle
  const toggleCollection = (id) => {
    setSelectedCollections(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  // NEW: Final deletion function
  const performClear = async () => {
    if (selectedCollections.length === 0) return;

    setIsDeleting(true);
    setClearStatus("Deleting data...");
    setSuccess("");
    setError("");

    try {
      const deletePromises = [];

      for (const collName of selectedCollections) {
        const collRef = collection(db, collName);
        const q = query(collRef);
        const snapshot = await getDocs(q);

        snapshot.docs.forEach(docSnap => {
          deletePromises.push(deleteDoc(doc(db, collName, docSnap.id)));
        });
      }

      await Promise.all(deletePromises);
      setSuccess("All selected data deleted successfully!");
      setShowModal(true);
      setTimeout(() => {
        setIsConfirmModalOpen(false);
        setIsClearModalOpen(false);
        setSelectedCollections([]);
        setClearStatus("");
      }, 2000);
    } catch (err) {
      setError("Error deleting data. Try again.");
      setShowModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading || authLoading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <p className="no-subjects">User data not available</p>;
  }

  return (
    <>
      <div className="dashboard-container padding-bottom-d sales-entry-head">
        {(userData.status === "Cashier" || userData.status === "Accountant" || userData.status === "Admin" || userData.status === "Supreme") && (
          <div className='all-categories'>
            {(userData.status === "Accountant" || userData.status === "Admin" || userData.status === "Supreme") && (
            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">FINANCIAL MANAGEMENT</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {pages.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-purple bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div>
            </div>
            )}

            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">CASH MANAGEMENT</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {pages1.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-light-blue-cash bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">ACCOUNTS PAYABLE (MONEY OUT)</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {pages2.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-teal bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">ACCOUNTS RECEIVABLE (MONEY IN)</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {pages3.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-red1 bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">SALES HISTORY</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                <Link to={``}
                  className="row align-center space-between first-cover monthly-report-button"
                  onClick={() => setIsDateModalOpen(true)}
                >
                  <span className="italics size2 font-orange bold">1.</span>  
                  <p className="second">Daily Sales</p>
                </Link>
                <Link to={``}
                  className="row align-center space-between first-cover monthly-report-button"
                  onClick={() => setIsMonthModalOpen(true)}
                >
                  <span className="italics size2 font-orange bold">2.</span>  
                  <p className="second">Monthly Sales</p>
                </Link>
              </div>
            </div>

            {(userData.status === "Accountant" || userData.status === "Admin" || userData.status === "Supreme") && (
            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">FINANCIAL REPORTS</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {financialReports.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-light-blue-cash bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div> 
            </div>
            )}

            {(userData.status === "Cashier" || userData.status === "Admin" || userData.status === "Supreme") && (
              <div className="categories-container">
                <p className="caps login-heading page-heading page-heading-d">PRODUCT MANAGEMENT</p>
                <div className={`categories-div categories-div-desktop categories-div-finance`}>
                  {pages5.map((page, index) => (
                    <Link
                      key={index}
                      className="row align-center space-between first-cover"
                      to={`/${page.link}`}
                    >
                      <span className="italics size2 font-gold bold">{page.icon}</span>
                      <p className="second">{page.name}</p>
                    </Link>
                  ))}

                  {/* PDF Button - Now correctly placed inside the div */}
                  <ProductsByCategoryPDFButton
                    products={managementData.products || []}
                    productsPerCategory={managementData.posStats?.productsPerCategory || {}}
                  />
                </div>
              </div>
            )}

            {(userData.status === "Admin" || userData.status === "Supreme") && (
            <div className="categories-container">
              <p className="caps login-heading page-heading page-heading-d">STAFF MANAGEMENT</p>
              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                {pages4.map((page, index) => (
                  <Link key={index} className="row align-center space-between first-cover" to={`/${page.link}`}>
                    <span className="italics size2 font-orange bold">{page.icon}</span>  
                    <p className="second">{page.name}</p>
                  </Link>
                ))}
              </div> 
            </div>
            )}

            {(userData.status === "Admin" || userData.status === "Supreme") && (
            <div className="categories-container" style={{ backgroundColor: '#e74d3c46'}}>
              <p className="caps login-heading page-heading page-heading-d" style={{ marginTop: '40px', color: '#e74c3c' }}>
                DANGER ZONE
              </p>

              <div 
                className={`categories-div categories-div-desktop categories-div-finance ${
                  pages.length >= 2 && pages.length <= 5 ? 'centered-items' : ''
                }`}
              >
                <Link to={``}
                  className="row align-center space-between first-cover first-cover-clear-all monthly-report-button"
                  onClick={() => setIsClearModalOpen(true)}
                >
                  <span className="italics size2 font-white bold">!</span>  
                  <p className="second font-white caps bold">SELECTIVE CLEAR DATA</p>
                </Link>
              </div>                     
            </div>
            )}
          </div>
        )}        

        {(userData.status !== "Admin" &&
          userData.status !== "Supreme" &&
          userData.status !== "Cashier" &&
          userData.status !== "Accountant") && (
          <p className="male-female-stat align-center justify-center">
            You don't have permission to view this page.
          </p>
        )}

        {/* MONTHLY SALES MODAL */}
        <Modal
          isOpen={isMonthModalOpen}
          onRequestClose={() => setIsMonthModalOpen(false)}
          contentLabel="Select Month"
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <p className="size5 bold caps">Select a Month</p>
          <div className="column modal-scroll">
            {months.map((month, index) => (
              <Link
                key={index}
                className="row align-center"
                to={`/sales-history?month=${month.value}`}
                onClick={() => setIsMonthModalOpen(false)}
              >
                <span className="italics size5 bold">{index + 1}. </span>  
                <p className="second">{month.name} {new Date().getFullYear()}</p>
              </Link>
            ))}
          </div>
          <button type="button" className="login-button caps" onClick={() => setIsMonthModalOpen(false)}>
            Close
          </button>
        </Modal>

        {/* DAILY SALES MODAL - DATE PICKER */}
        <Modal
          isOpen={isDateModalOpen}
          onRequestClose={() => setIsDateModalOpen(false)}
          contentLabel="Select Date"
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <h2 className="size5 bold caps mb-20">Select Date for Daily Sales</h2>
          
          <div className="form-group column align-center">
            <label className="input-label mb-10">Choose Date</label>
            <input
              type="date"
              className="input-field"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]} // Can't select future dates
              style={{ width: "100%", maxWidth: "300px" }}
            />
          </div>

          <div className="row justify-center gap-15 mt-30">
            <button
              type="button"
              className="login-button caps"
              onClick={() => setIsDateModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={handleDateSelect}
              disabled={!selectedDate}
            >
              View Daily Sales
            </button>
          </div>
        </Modal>

        {/* NEW: SELECTIVE CLEAR MODAL */}
        <Modal
          isOpen={isClearModalOpen}
          onRequestClose={() => !isDeleting && setIsClearModalOpen(false)}
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <h2 className="size5 bold caps" style={{ color: '#e74c3c' }}>
            Selective Data Clear
          </h2>
          <p className="size3">
            Choose which data you want to permanently delete:
          </p>

          <div className="column gap-15" style={{ margin: '20px 0' }}>
            {collectionsToClear.map(coll => (
              <label key={coll.id} className="row align-center gap-10" style={{ fontSize: '16px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(coll.id)}
                  onChange={() => toggleCollection(coll.id)}
                  disabled={isDeleting}
                />
                <span>{coll.label}</span>
              </label>
            ))}
          </div>

          <div className="row justify-center gap-15">
            <button
              className="login-button caps"
              onClick={() => setIsClearModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              className="login-button caps"
              style={{ backgroundColor: '#e74c3c' }}
              onClick={() => {
                setError("");
                if (selectedCollections.length === 0) {
                  setError("Please select at least one item");
                  setShowModal(true);
                  return;
                }
                setIsClearModalOpen(false);
                setIsConfirmModalOpen(true);
              }}
              disabled={isDeleting}
            >
              Next
            </button>
          </div>
        </Modal>

        {/* NEW: CONFIRMATION MODAL */}
        <Modal
          isOpen={isConfirmModalOpen}
          onRequestClose={() => !isDeleting && setIsConfirmModalOpen(false)}
          className="custom-modal"
          overlayClassName="custom-modal-overlay"
        >
          <div className="column align-center">
            <i className="fas fa-exclamation-triangle fa-4x" style={{ color: '#e74c3c', marginBottom: '20px' }}></i>
            <h2 className="size5 bold caps" style={{ color: '#e74c3c' }}>
              Confirm Deletion
            </h2>
            <p className="size3 text-center" style={{ margin: '20px 0', maxWidth: '300px' }}>
              You are about to <strong>permanently delete</strong>:
            </p>
            <div className="column align-start gap-5" style={{ background: '#ffebee', padding: '15px', borderRadius: '8px', width: '100%', maxWidth: '300px' }}>
              {selectedCollections.map(id => {
                const found = collectionsToClear.find(c => c.id === id);
                return <span key={id} style={{ fontWeight: 'bold' }}>• {found?.label}</span>;
              })}
            </div>
            <p className="size3 text-center">
              This action <strong>cannot be undone</strong>.
            </p>

            {clearStatus && (
              <p style={{ color: isDeleting ? '#f39c12' : '#27ae60', fontWeight: 'bold', margin: '15px 0' }}>
                {clearStatus}
              </p>
            )}

            <div className="row justify-center gap-1">
              <button
                className="login-button caps"
                onClick={() => setIsConfirmModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="login-button caps"
                style={{ backgroundColor: '#c0392b' }}
                onClick={performClear}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete Forever"}
              </button>
            </div>
          </div>
        </Modal>
      </div>
      {showModal && 
        <SuccessModal
          icon="xmark"
          iconColor="red"
          message={error}
          onClose={() => {
            setError("");
            setShowModal(false);
          }}
        />
      }
    </>
  );
};

export default Management;
